/**
 * Sharing a pun outwards.
 *
 * Two halves that have to agree: the text a player sends, and the URL that text
 * points at. The URL is also what `functions/src/share.ts` renders link previews
 * for, so the shape `/p/{punId}` is fixed in both places — change one and the
 * other stops matching.
 */
import type { Pun } from '../../shared/schemas';
import { promptSentence } from '../../shared/prompt';

/** Where a pun lives on the public web. */
export function punShareUrl(punId: string): string {
  return `${window.location.origin}/p/${punId}`;
}

/**
 * The message body, without the URL.
 *
 * `navigator.share` takes `text` and `url` separately and every target joins
 * them its own way, so the URL must not be baked in here — the clipboard
 * fallback appends it instead.
 *
 * `authorName` is omitted for your own pun. "— You" reads as a mistake in
 * someone else's group chat, and the person sharing is the attribution.
 */
export function punShareText(pun: Pun, authorName?: string | null): string {
  const prompt = promptSentence(pun.category, pun.output);
  const credit = authorName ? ` — ${authorName}` : '';

  return `"${pun.text}"\n\n${prompt}${credit}`;
}

export type ShareOutcome = 'shared' | 'copied' | 'dismissed' | 'failed';

/** A share sheet the player closed without choosing anything. */
function wasDismissed(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * Hand a pun to the OS share sheet, or failing that the clipboard.
 *
 * Returns what actually happened rather than throwing, because all three
 * outcomes need different words on screen and none of them is exceptional.
 *
 * The order matters: `navigator.share` must be attempted first and directly
 * inside the click, since browsers only allow it from a user gesture. Awaiting
 * anything before it spends the gesture and the call is rejected.
 */
export async function sharePun(pun: Pun, authorName?: string | null): Promise<ShareOutcome> {
  const url = punShareUrl(pun.id);
  const text = punShareText(pun, authorName);

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: 'Pun and Done', text, url });
      return 'shared';
    } catch (error) {
      // Closing the sheet is a decision, not a failure — falling through to the
      // clipboard would put a pun on it that nobody asked to copy.
      if (wasDismissed(error)) return 'dismissed';
      console.error('[share] share sheet failed, falling back to clipboard:', error);
    }
  }

  try {
    await navigator.clipboard.writeText(`${text}\n\n${url}`);
    return 'copied';
  } catch (error) {
    console.error('[share] could not copy:', error);
    return 'failed';
  }
}
